import React from "react";
import { IRegisteredUser } from "../../models/epcCustomer.model";
import { IKycDocument } from "../../models/epcCustomer.model";
import { updateKycDocument } from "../../store/slices/epcSlice";
import { RootDispatch, RootState } from "../../store";
import { useDispatch, useSelector } from "react-redux";

interface UserDocsProps {
  customer: IRegisteredUser | null;
  handleCustomerClick: (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    documentUrl: string
  ) => void;
  currentUserRole: string | undefined;
}

const UserDocsRender: React.FC<UserDocsProps> = ({
  customer,
  handleCustomerClick,
  currentUserRole,
}) => {
  const dispatch: RootDispatch = useDispatch();
  const verifyingDocumentLoading = useSelector(
    (state: RootState) => state.epc.loadingState.verifyingDocumentLoading
  );
  const registeredUsers = useSelector(
    (state: RootState) => state.epc.registeredUserInfoWithDetails
  );

  const documentTypes: IKycDocument[] = React.useMemo(() => {
    if (!customer) return [];
    const found = registeredUsers.find((u) => u._id === customer._id);
    return (found?.kyc || []) as IKycDocument[];
  }, [registeredUsers, customer]);

  const handleVerifyDocument = async (
    documentId: string,
    isVerified: boolean
  ) => {
    if (!customer?._id) return;

    try {
      await dispatch(
        updateKycDocument({
          customerId: customer._id,
          documentId,
          isVerified,
        })
      ).unwrap();
    } catch (error) {
      console.error("Failed to verify document:", error);
    }
  };

  const getDocumentLoadingState = (documentId: string) => {
    return verifyingDocumentLoading[documentId] || false;
  };

  return (
    <>
      {documentTypes?.length > 0 ? (
        documentTypes.map((docType, index) => {
          const hasDocument = docType.link;
          const isVerifying = getDocumentLoadingState(docType._id ?? "");

          return (
            <div
              key={index}
              onClick={(e) => handleCustomerClick(e, docType.link)}
              className={`relative bg-white rounded-lg border p-4 transition-all duration-200 hover:shadow-md flex flex-col items-center text-center ${
                hasDocument
                  ? "border-gray-200 hover:border-gray-300 cursor-pointer shadow-sm"
                  : "border-gray-200 border-dashed"
              }`}
            >
              {!docType.isVerified &&
                hasDocument &&
                currentUserRole !== "account" &&
                currentUserRole !== "dispatch-lv1" &&
                currentUserRole !== "dispatch-lv2" && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleVerifyDocument(docType._id ?? "", true);
                    }}
                    disabled={isVerifying}
                    className="absolute top-1 -left-2 bg-gradient-to-br from-emerald-400 to-emerald-600 text-white text-[10px] font-bold px-2 py-1 rounded rotate-[-30deg] shadow-xs hover:rotate-0 hover:scale-105 transition-all tracking-wider"
                  >
                    {isVerifying ? (
                      <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      "VERIFY"
                    )}
                  </button>
                )}
              {/* Icon */}
              <div className="p-2 mb-3">
                <svg
                  className="w-6 h-6 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>

              {/* Document Name */}
              <span
                className={`text-sm font-medium ${
                  hasDocument ? "text-gray-900" : "text-gray-500"
                }`}
              >
                {docType.name}
              </span>

              {/* Status Text */}
              {!hasDocument && (
                <span className="text-xs text-red-500 mt-1">Missing</span>
              )}

              {!docType.isVerified && hasDocument && (
                <div className="absolute top-2 right-2 bg-red-100 p-1 rounded-full shadow-sm">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="w-3.5 h-3.5 text-red-600"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10.29 3.86a1.5 1.5 0 012.42 0l8.15 11.82c.67.97-.02 2.32-1.21 2.32H3.36c-1.19 0-1.88-1.35-1.21-2.32l8.14-11.82zM11 9a1 1 0 112 0v4a1 1 0 11-2 0V9zm1 8a1.25 1.25 0 100-2.5A1.25 1.25 0 0012 17z"
                    />
                  </svg>
                </div>
              )}
              {docType.isVerified === true && (
                <div className="absolute top-2 right-2 bg-green-100 p-1 rounded-full shadow-sm">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    className="w-3.5 h-3.5 text-green-600"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.603 3.799A4.49 4.49 0 0112 2.25c1.357 0 2.573.6 3.397 1.549a4.49 4.49 0 013.498 1.307 4.491 4.491 0 011.307 3.497A4.49 4.49 0 0121.75 12a4.49 4.49 0 01-1.549 3.397 4.491 4.491 0 01-1.307 3.497 4.491 4.491 0 01-3.497 1.307A4.49 4.49 0 0112 21.75a4.49 4.49 0 01-3.397-1.549 4.49 4.49 0 01-3.498-1.306 4.491 4.491 0 01-1.307-3.498A4.49 4.49 0 012.25 12c0-1.357.6-2.573 1.549-3.397a4.49 4.49 0 011.307-3.497 4.49 4.49 0 013.497-1.307zm7.007 6.387a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
              )}
            </div>
          );
        })
      ) : (
        // No Documents Found State
        <div className="col-span-full flex flex-col items-center justify-center py-12 px-6">
          <div className="p-4 bg-white rounded-lg border border-gray-200 mb-4">
            <svg
              className="w-8 h-8 text-gray-400 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>

          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            No Documents Found
          </h3>
          <p className="text-gray-600 text-center text-sm max-w-sm">
            No documents are currently available. Please check back later.
          </p>
        </div>
      )}
    </>
  );
};

export default UserDocsRender;
